import { Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import {
  useEffect,
  useId,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FormSheet,
  FormSheetBody,
  FormSheetFooter,
  FormSheetHeader,
} from "@/components/atomic-crm/misc/FormSheet";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ideaFormDefaults,
  normalizeIdeaForm,
  type IdeaFormValues,
} from "./ideas";
import {
  IDEA_STATUSES,
  ideaStatusLabelKey,
  PRIORITIES,
  priorityLabelKey,
} from "./labels";
import type { MarketIdea } from "./types";

const SELECT_CLASS =
  "h-10 w-full rounded-lg border border-input bg-transparent px-3 text-base outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:h-9 md:text-sm dark:bg-input/30";

interface IdeaFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edits this idea; creates a new one when absent. */
  idea?: MarketIdea | null;
}

/**
 * Create / edit an idea: a dialog on desktop, the app's full-screen
 * FormSheet on a phone.
 */
export const IdeaForm = ({ open, onOpenChange, idea }: IdeaFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const [values, setValues] = useState<IdeaFormValues>(() =>
    ideaFormDefaults(idea ?? undefined),
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [create, { isPending: creating }] = useCreate<MarketIdea>();
  const [update, { isPending: updating }] = useUpdate<MarketIdea>();
  const [remove, { isPending: deleting }] = useDelete<MarketIdea>();

  useEffect(() => {
    if (open) {
      setValues(ideaFormDefaults(idea ?? undefined));
      setError(null);
      setConfirmDelete(false);
    }
  }, [open, idea]);

  const set =
    <K extends keyof IdeaFormValues>(key: K) =>
    (value: IdeaFormValues[K]) =>
      setValues((current) => ({ ...current, [key]: value }));

  const close = () => onOpenChange(false);
  const onError = () => notify("market.ideas.error", { type: "error" });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeIdeaForm(values);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (idea) {
      update(
        "market_ideas",
        { id: idea.id, data: result.data, previousData: idea },
        {
          mutationMode: "optimistic",
          onSuccess: () => notify("market.ideas.updated", { type: "success" }),
          onError,
        },
      );
      close();
    } else {
      create(
        "market_ideas",
        { data: result.data },
        {
          onSuccess: () => {
            notify("market.ideas.created", { type: "success" });
            close();
          },
          onError,
        },
      );
    }
  };

  const onDelete = () => {
    if (!idea) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    remove(
      "market_ideas",
      { id: idea.id, previousData: idea },
      {
        mutationMode: "optimistic",
        onSuccess: () => notify("market.ideas.deleted", { type: "info" }),
        onError,
      },
    );
    close();
  };

  const title = translate(idea ? "market.ideas.edit" : "market.ideas.new");
  const busy = creating || updating || deleting;

  const fields = (
    <>
      <Field
        id={`${formId}-title`}
        label={translate("market.ideas.form.title")}
      >
        <Input
          id={`${formId}-title`}
          value={values.title}
          onChange={(event) => {
            set("title")(event.target.value);
            setError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${formId}-error` : undefined}
          autoFocus={!isMobile}
          maxLength={240}
        />
        {error ? (
          <p id={`${formId}-error`} className="text-sm text-destructive">
            {translate(error)}
          </p>
        ) : null}
      </Field>
      <Field
        id={`${formId}-description`}
        label={translate("market.ideas.form.description")}
      >
        <Textarea
          id={`${formId}-description`}
          value={values.description}
          onChange={(event) => set("description")(event.target.value)}
          rows={5}
          aria-describedby={`${formId}-description-hint`}
        />
        <p
          id={`${formId}-description-hint`}
          className="text-xs text-muted-foreground"
        >
          {translate("market.ideas.form.description_hint")}
        </p>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          id={`${formId}-status`}
          label={translate("market.idea_status.label")}
        >
          <select
            id={`${formId}-status`}
            className={SELECT_CLASS}
            value={values.status}
            onChange={(event) =>
              set("status")(event.target.value as IdeaFormValues["status"])
            }
          >
            {IDEA_STATUSES.map((status) => (
              <option key={status} value={status}>
                {translate(ideaStatusLabelKey(status))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-priority`}
          label={translate("market.priority.label")}
        >
          <select
            id={`${formId}-priority`}
            className={SELECT_CLASS}
            value={values.priority}
            onChange={(event) =>
              set("priority")(event.target.value as IdeaFormValues["priority"])
            }
          >
            {PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {translate(priorityLabelKey(priority))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-sector`}
          label={translate("market.ideas.form.sector")}
        >
          <Input
            id={`${formId}-sector`}
            value={values.sector}
            onChange={(event) => set("sector")(event.target.value)}
          />
        </Field>
        <Field
          id={`${formId}-product`}
          label={translate("market.ideas.form.product_type")}
        >
          <Input
            id={`${formId}-product`}
            value={values.product_type}
            onChange={(event) => set("product_type")(event.target.value)}
          />
        </Field>
      </div>
    </>
  );

  const deleteButton = idea ? (
    <Button
      type="button"
      variant="destructive"
      onClick={onDelete}
      disabled={busy}
      className="mr-auto rounded-full"
    >
      <Trash2 aria-hidden className="size-4" />
      {translate(
        confirmDelete ? "market.ideas.delete_confirm" : "market.ideas.delete",
      )}
    </Button>
  ) : null;

  const actions = (
    <>
      {deleteButton}
      <Button
        type="button"
        variant="outline"
        className="rounded-full max-md:hidden"
        onClick={close}
      >
        {translate("market.ideas.form.cancel")}
      </Button>
      <Button
        type="submit"
        form={formId}
        disabled={busy}
        className="btn-save rounded-full px-5 max-md:flex-1"
      >
        {translate("market.ideas.form.save")}
      </Button>
    </>
  );

  if (isMobile)
    return (
      <FormSheet open={open} onOpenChange={onOpenChange}>
        <FormSheetHeader title={title} />
        <form
          id={formId}
          onSubmit={submit}
          className="flex min-h-0 flex-1 flex-col"
          noValidate
        >
          <FormSheetBody>{fields}</FormSheetBody>
        </form>
        <FormSheetFooter className="flex-wrap">{actions}</FormSheetFooter>
      </FormSheet>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form id={formId} onSubmit={submit} className="space-y-3" noValidate>
          {fields}
        </form>
        <DialogFooter className="flex-wrap gap-2">{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);
